import { ZodiacSign, AppSettings } from '../types';

const SETTINGS_KEY = 'zodiage_settings_v1';

export const getTodayString = (): string => {
    // Return YYYY-MM-DD in LOCAL time
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const getInitialSettings = (): AppSettings => {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) {
        const parsed = JSON.parse(saved);
        return {
            ...parsed,
            // Ensure new fields exist for old saves
            lastOracleAskTimestamp: parsed.lastOracleAskTimestamp || null,
            lastOracleResponse: parsed.lastOracleResponse || null,
            lastSpreadPickTimestamp: parsed.lastSpreadPickTimestamp || null,
            lastSpreadCards: parsed.lastSpreadCards || null,
            lastSpreadRevealed: parsed.lastSpreadRevealed || null,
            lastReflectionTimestamp: parsed.lastReflectionTimestamp || null,
            lastReflectionContent: parsed.lastReflectionContent || null,
            lastCompatibilityTimestamp: parsed.lastCompatibilityTimestamp || null,
            lastCompatibilityResult: parsed.lastCompatibilityResult || null,
            lastPersonalityTimestamp: parsed.lastPersonalityTimestamp || null,
            lastPersonalityProfile: parsed.lastPersonalityProfile || null,
            lastTimelineTimestamp: parsed.lastTimelineTimestamp || null,
            readingHistory: parsed.readingHistory || []
        };
    }
    return {
        soundEnabled: true, // Set to true by default for a better first-time experience
        nightMode: false,
        tarotCollection: [],
        lastDailyReadingDate: null,
        dailyCardId: null,
        dailyCardReversed: false,
        lastTarotPickTimestamp: null,
        lastOracleAskTimestamp: null,
        lastOracleResponse: null,
        currentMood: null,
        unlockedSpreads: [] as string[],
        lastSpreadPickTimestamp: null,
        lastSpreadCards: null,
        lastSpreadRevealed: null,
        lastReflectionTimestamp: null,
        lastReflectionContent: null,
        lastCompatibilityTimestamp: null,
        lastCompatibilityResult: null,
        lastPersonalityTimestamp: null,
        lastPersonalityProfile: null,
        lastTimelineTimestamp: null,
        readingHistory: []
    };
};

export const saveSettings = (settings: AppSettings) => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
};

export const getCurrentSeasonSign = (): ZodiacSign => {
    const now = new Date();
    const day = now.getDate();
    const month = now.getMonth() + 1;

    if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return ZodiacSign.Aries;
    if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return ZodiacSign.Taurus;
    if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return ZodiacSign.Gemini;
    if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return ZodiacSign.Cancer;
    if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return ZodiacSign.Leo;
    if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return ZodiacSign.Virgo;
    if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return ZodiacSign.Libra;
    if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return ZodiacSign.Scorpio;
    if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return ZodiacSign.Sagittarius;
    if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return ZodiacSign.Capricorn;
    if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return ZodiacSign.Aquarius;
    return ZodiacSign.Pisces;
};

// Generates a pseudo-random energy score (1-100) based on date and birthDate
export const calculateDailyEnergy = (birthDate: Date | null): number => {
    const today = new Date();
    const day = today.getDate();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    
    // Base entropy from current date
    let seed = day * month * year;
    
    // Add user entropy if available
    if (birthDate) {
        seed += birthDate.getDate() * (birthDate.getMonth() + 1);
    }

    // Simple pseudo-random function
    const x = Math.sin(seed) * 10000;
    const result = Math.floor((x - Math.floor(x)) * 100);
    
    // Ensure 1-100
    return Math.max(1, Math.min(100, result));
};

export const getTimeUntilAvailable = (lastTimestamp: number | null): { locked: boolean, text: string } => {
    if (!lastTimestamp) return { locked: false, text: '' };
    
    // 3 Hours Cooldown
    const COOLDOWN = 3 * 60 * 60 * 1000; 
    const now = Date.now();
    const diff = (lastTimestamp + COOLDOWN) - now;
    
    if (diff <= 0) return { locked: false, text: '' };
    
    const h = Math.floor(diff / (1000 * 60 * 60));
    const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const s = Math.floor((diff % (1000 * 60)) / 1000);
    
    return { 
        locked: true, 
        text: `${h}h ${m}m ${s}s` 
    };
};